import { getSerialized } from '@extension/wa-communication-layer/core/utils/wa-key-serialization';
import { Subscription } from "../../bt-common/schemas/bt/subscription";
import { SubscriptionObject } from "../../bt-common/services/subscription-object.class";
import { computeAccountBlocked, computeDunning, DunningState } from "./account-blocked.util";
import { User, UserInterface } from "@common-models/user";
import { Workspace } from "@common-models/workspace";
import { createSelector } from "@reduxjs/toolkit";
import { isDisplayConnected, selectNow } from "@common/reducers/engine-display-status";
import { debugLog } from "@common/utils/debug-logger";
import { selectMergedContactMap } from "@common/reducers/WhatsAppReducer";
import { resolveSenderJidEquivalents } from "@common/utils/jid-equivalents";
import { countryOfPhone } from "@common/utils/phone-validation-utils";
import sift from 'sift';

import {
  logsSelectByQuery,
  subscriptionSelectByQuery,
  userSelectByQuery,
  workspaceSelectOneObjectByQuery,
  workspaceUserSelectOneObjectByQuery
} from "@common-reducers/DBServiceThunks";
import { CommonRootState } from "@common/types/common-root-state-type";
import moment from "moment";
import { GenericState } from "./DBServiceReducersInterfaces";

import {
  sessionSelectOneObjectByQuery,
  userSelectOneObjectByQuery
} from "@common-reducers/DBServiceThunks";
import { getDarkModeFromBodyClass } from "../components/bt-theme-provider";

// Selector to get messages for current chat
export const selectMessagesForCurrentChat = createSelector(
  [
    (state: CommonRootState) => state.DBUserMessageReducer.entities,
    (state: CommonRootState) => getSerialized(state.WhatsAppReducer.currentChat?.id),
    // #165 — the merged WA/imported contact map, so the recipient match below can be
    // @lid<->@c.us equivalence-aware (same map the cancel path uses).
    selectMergedContactMap,
  ],
  (messageEntities, currentChatId, mergedContactMap) => {
    if (!currentChatId) return [];

    // #165 — LID accounts carry the open chat's id as either `…@lid` or `…@c.us`, while
    // the stored recipient id was migrated to the phone form by getPendingUserMessages.
    // The two settle via independent async paths, so an EXACT string match transiently
    // drops the (intact) pending message from this sidebar — it flashes then vanishes,
    // though nothing is deleted. Match against the equivalence SET instead, exactly as
    // the cancel path (resolveSenderJidEquivalents) already does. Non-LID chats resolve
    // to a single-element set, so this is a no-op for them.
    const currentChatIdEquivalents = resolveSenderJidEquivalents(currentChatId, mergedContactMap);

    const allMessages = Object.keys(messageEntities)
      .map(id => messageEntities[id])
      .filter((m): m is NonNullable<typeof m> => !!m);

    const filteredMessages = allMessages.filter(sift({
      $or: [
        { deleted: { $exists: false } },
        { deleted: false },
      ],
      // #166: `sent_pending_ack` and legacy `sent` are included deliberately.
      // Both mean "handed to WhatsApp, not yet confirmed delivered" — which
      // bt-common defines as NOT yet delivered. Excluding them made those
      // messages vanish from this sidebar entirely: neither listed nor shown
      // as sent. getEffectiveMessageStatus maps both to "unconfirmed", and
      // MESSAGE_STATUS_CONFIG has a dedicated `unconfirmed` entry (see
      // message-status-config.ts) so they render as a distinct in-flight
      // badge — not the "pending" fallback.
      // `confirmed`/`received` remain excluded — whether a sent list belongs on
      // this surface is a separate, open decision.
      status: { $in: ["pending", "failed", "sent_pending_ack", "sent"] },
      contactList: { $elemMatch: { id: { $in: currentChatIdEquivalents } } }
    }));
    
    // Sort by dueDate from earliest to latest
    return filteredMessages.sort((a, b) => {
      // Messages without dueDate go to the end
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      
      // Both have dueDate, sort by earliest first
      const dateA = new Date(a.dueDate).getTime();
      const dateB = new Date(b.dueDate).getTime();
      return dateA - dateB;
    });
  }
);

// Selector to get the length of messages for current chat
export const selectMessagesForCurrentChatLength = createSelector(
  [selectMessagesForCurrentChat],
  (messages) => messages.length
);

// Selector to get message counts grouped by status
export interface MessageCountsByStatus {
  pending: number;
  failed: number;
  /** #166 rev2 — `sent_pending_ack` AND legacy `sent`, both displayed as "unconfirmed"
   *  (field name kept as `sending` for API stability with existing consumers). Both
   *  statuses are counted here so `total` remains the sum of its parts. */
  sending: number;
  total: number;
}

export const selectMessageCountsByStatus = createSelector(
  [selectMessagesForCurrentChat],
  (messages): MessageCountsByStatus => {
    const counts = {
      pending: 0,
      failed: 0,
      sending: 0,
      total: messages.length
    };

    messages.forEach((message) => {
      if (message.status === 'pending') {
        counts.pending++;
      } else if (message.status === 'failed') {
        counts.failed++;
      } else if (message.status === 'sent_pending_ack' || message.status === 'sent') {
        // #166 rev2 — legacy `sent` rows are the same "unconfirmed" bucket as
        // sent_pending_ack (see selectMessagesForCurrentChat above). Both must land
        // here or `total` stops being the sum of pending+failed+sending.
        counts.sending++;
      }
    });

    return counts;
  }
);

// Selector to get filtered messages based on visibility statuses
// visibleStatuses parameter should be passed from component state
export const selectFilteredMessagesForCurrentChat = createSelector(
  [
    selectMessagesForCurrentChat,
    (_state: CommonRootState, visibleStatuses: { pending: boolean; failed: boolean }) => visibleStatuses
  ],
  (messages, visibleStatuses) => {
    return messages.filter((message) => {
      if (message.status === 'pending') {
        return visibleStatuses.pending;
      } else if (message.status === 'failed') {
        return visibleStatuses.failed;
      }
      // #166: `sent_pending_ack` reaches here and is always shown — it is not
      // governed by the pending/failed toggles, because the user cannot act on
      // an in-flight send. Before the widening above, this branch was
      // unreachable: the source selector only ever emitted pending/failed.
      return true;
    });
  }
);

/**
 * The messages "Clear all" is allowed to remove: strictly those that have NOT
 * gone out.
 *
 * Deliberately NARROWER than `selectMessagesForCurrentChat`, which also surfaces
 * `sent_pending_ack` (#166). Visibility and deletability are different questions:
 * making sent-but-unacked messages visible must not turn "Clear all" into
 * something that destroys records of messages already handed to WhatsApp.
 * #149 is the ticket about what it costs when a delete quietly covers more than
 * the user chose — do not "simplify" this back into the display selector.
 */
export const selectClearableMessagesForCurrentChat = createSelector(
  [selectMessagesForCurrentChat],
  (messages) => messages.filter(
    (message) => message.status === 'pending' || message.status === 'failed'
  )
);

/** #708 — how many of the clearable (pending/failed) messages are recurring-series heads.
 *  Deleting one ends the whole series, so the Clear dialogs warn with these counts. */
export interface ClearableRecurringCounts { pending: number; failed: number; all: number }
export const selectClearableRecurringCounts = createSelector(
  [selectClearableMessagesForCurrentChat],
  (messages): ClearableRecurringCounts => {
    const counts = { pending: 0, failed: 0, all: 0 };
    messages.forEach((m) => {
      if (!m.isRecurring) return;
      counts.all++;
      if (m.status === 'pending') counts.pending++;
      else if (m.status === 'failed') counts.failed++;
    });
    return counts;
  }
);

export const selectCurrentUser = createSelector(
  [
    (state: CommonRootState) => state.DBUserReducer,
    (state: CommonRootState) => state.UserReducer?.userId,
    (state: CommonRootState) => state.UserReducer?.user,
  ],
  (state: GenericState<UserInterface>, userId: string | undefined, directUser: UserInterface | undefined): UserInterface | undefined => {
    if (directUser) return directUser;
    const user = userSelectOneObjectByQuery(state, { _id: userId });
    return user;
  }
);

// Other code such as selectors can use the imported `CommonRootState` type
export const selectUser = createSelector(
  [(state: CommonRootState) => state],
  (state: CommonRootState): UserInterface | undefined => {
    const directUser = state?.UserReducer?.user;
    if (directUser) return directUser;
    const [user] = userSelectByQuery(state.DBUserReducer, {
      _id: state.UserReducer.userId,
    });
    return user;
  }
);

export const isLoggedInSelector = createSelector(
  [(state: CommonRootState) => state, (state: CommonRootState) => selectUser(state)],
  (_state: CommonRootState, user: UserInterface | undefined) => {
    return !!user;
  }
);

export const isLabelsEnabledSelector = createSelector(
  [(state: CommonRootState) => state, (state: CommonRootState) => selectUser(state)],
  (_state: CommonRootState, user: UserInterface | undefined) => {
    const userIsLoggedIn = !!user?._id;
    return userIsLoggedIn && !user?.isLabelsDisabled;
  }
);


export const selectCurrentUserPhone = createSelector(
  [
    (state: CommonRootState) => state.DBUserReducer,
    (state: CommonRootState) => selectCurrentUser(state),
  ],
  (_state: GenericState<UserInterface>, user: UserInterface | undefined) => {
    return user?.phone;
  }
);


export const selectCurrentUserEmail = createSelector(
  [
    (state: CommonRootState) => state.DBUserReducer,
    (state: CommonRootState) => selectCurrentUser(state),
  ],
  (_state: GenericState<UserInterface>, user: UserInterface | undefined) => {
    return user?.email;
  }
);

export const selectUserObject = createSelector(
  [selectCurrentUser],
  (user: UserInterface | undefined) => {
    return user ? new User(user) : undefined;
  }
);

export const selectIsDarkMode = createSelector(
  [(state: CommonRootState) => state.UserReducer.user],
  (user) => {
    return user?.isDarkMode || false;
  }
);

// Keep-awake defaults ON: only an explicit `false` disables it.
// Reads DIRECTLY from the canonical DBUserReducer entity (NOT selectCurrentUser,
// which prefers the stale UserReducer.user copy). Incoming cross-client MQTT
// updates (users patch -> conditionalUpdateOne) land in DBUserReducer, so this
// is the only source that flips the toggle live on every tab/device.
export const selectKeepWhatsappAwake = createSelector(
  [
    (state: CommonRootState) => state.DBUserReducer,
    (state: CommonRootState) => state.UserReducer?.userId,
  ],
  (dbUser, userId) => {
    const user = userId ? userSelectOneObjectByQuery(dbUser, { _id: userId }) : undefined;
    // Default OFF (opt-in): the tab-wake steals focus for offline/gateway users,
    // so only an explicit `true` shows the toggle on / keeps the tab awake.
    return user?.keepWhatsappTabAwake === true;
  }
);

// btgpt "Files auto-recognition" — defaults ON: only an explicit `false`
// disables it. Same DBUserReducer-direct read as selectKeepWhatsappAwake so
// cross-client MQTT patches flip it live (btgpt web toggle → WA tab handler).
export const selectBtgptFilesAutoRecognition = createSelector(
  [
    (state: CommonRootState) => state.DBUserReducer,
    (state: CommonRootState) => state.UserReducer?.userId,
  ],
  (dbUser, userId) => {
    const user = userId ? userSelectOneObjectByQuery(dbUser, { _id: userId }) : undefined;
    return user?.btgptFilesAutoRecognition !== false;
  }
);

export const selectIsSystemDarkMode = createSelector(
  [(state: CommonRootState) => state.UserReducer.whatsappDarkMode],
  (whatsappDarkMode) => {
    return whatsappDarkMode;
  }
);

/**
 * The country a number the user types is read in: their own WhatsApp number's
 * country first (an Israeli user types "054…"), then the last one they picked in
 * the start-chat dialog. undefined = unknown: a local number then needs its +code.
 */
export const selectDefaultPhoneCountry = createSelector(
  [selectCurrentUserPhone, (state: CommonRootState) => selectUser(state)?.defaultChatCountryCode],
  (phone, picked): string | undefined => countryOfPhone(phone) || picked || undefined
);

export const selectCurrentWorkspace = createSelector(
  [(state: CommonRootState) => state, selectUserObject],
  (state: CommonRootState, user) => {
    const currentWorkspace = workspaceSelectOneObjectByQuery(
      state.DBWorkspaceReducer,
      { _id: user?.currentWorkspace }
    );
    return currentWorkspace;
  }
);

//create selector
export const selectUserEngagementScore = createSelector(
  [(state: CommonRootState) => state, selectUserObject],
  (state: CommonRootState, user) => {
    const userLogs = logsSelectByQuery(state.DBLogReducer, {
      triggerById: user?._id,
    });

    const totalUserEngagementScore = userLogs.reduce(
      (accumulator, currentObject) => {
        return accumulator + (currentObject.userEngagementPoints || 0);
      },
      0
    );

    return totalUserEngagementScore;
  }
);
export const isUserWorkSpaceOwner = createSelector(
  [(state: CommonRootState) => state, selectUserObject],
  (state: CommonRootState, user) => {
    let isOwner = false;
    const currentWorkspace = workspaceSelectOneObjectByQuery(
      state.DBWorkspaceReducer,
      { _id: user?.currentWorkspace }
    );

    if (currentWorkspace && user) {
      isOwner = currentWorkspace.owner === user._id;
    }
    return isOwner;
  }
);

export const selectIsWorkspaceChanging = createSelector(
  [(state: CommonRootState) => state, selectUserObject, selectCurrentWorkspace],
  (state: CommonRootState, user, currentWorkspace) => {
    return currentWorkspace?._id !== user?.currentWorkspace;
  }
);

export const isCurrentUserWorkSpaceOwner = createSelector(
  (state: CommonRootState) => state,
  selectUserObject,
  selectCurrentWorkspace,
  (state: CommonRootState, user: User | undefined, currentWorkspace: Workspace | undefined) => {
    let isCurrentUserWorkSpaceOwner = false;
    if (user) {
      const workspaceOwner = workspaceUserSelectOneObjectByQuery(
        state.DBWorkspaceUserReducer,
        { userId: user?._id, workspaceId: currentWorkspace?._id }
      );
      isCurrentUserWorkSpaceOwner = !!workspaceOwner;
    }
    return isCurrentUserWorkSpaceOwner;
  }
);

export const selectCurrentUserWorkspace = createSelector(
  (state: CommonRootState) => state,
  selectUserObject,
  (state: CommonRootState, user: User | undefined) => {
    const workspace = workspaceSelectOneObjectByQuery(
      state.DBWorkspaceReducer,
      { owner: user?._id }
    );
    return workspace;
  }
);

export const subscriptionObjectListSelector = createSelector(
  (state: CommonRootState) => state.DBSubscriptionReducer,
  (subscriptionReducer) =>
    (subscriptionSelectByQuery(subscriptionReducer, {}) ?? []).map(
      (subscription) => new SubscriptionObject(subscription)
    )
);

/**
 * FE#167 — the subscription list for the CURRENT workspace.
 *
 * THE BUG THIS FIXES: this used to return `[]` whenever the workspace DOC was not in
 * `DBWorkspaceReducer` yet, so every downstream entitlement selector reported "no
 * subscription" for a paying customer during boot. That window is GUARANTEED, not
 * intermittent: the auth pipeline awaits `DBSubscriptionThunks.find({})`
 * (backend-api-query-handlers.ts:196) and only then `getWorkspaceDataThunk` (~:219), so
 * the paid subscription is in the store BEFORE the workspace doc that is used to filter
 * it. Pedro (#167) was walled by exactly that ordering.
 *
 * WHY THE FALLBACK IS SAFE: the client-side workspace filter is REDUNDANT. The server
 * already scopes `/subscriptions` to `user.currentWorkspace`
 * (subscriptions.hooks.ts:183 `workspaceSubscriptionList(user.currentWorkspace, ...)`)
 * and stamps `workspaceId` on every row it returns (workspaces.model.ts:163). So the
 * rows in the store ARE this workspace's rows; re-filtering them against a doc we have
 * not loaded yet only ever subtracts truth.
 *
 * WHY IT IS GATED ON `itemsLoaded`, not on `!currentWorkspace`: `switchWorkspaceThunk`
 * does NOT clear `DBSubscriptionReducer`, so rows from a previously-paid workspace can
 * linger. Falling back on *any* missing workspace doc would let those stale rows confer
 * paid inside an unpaid workspace. Restricting the fallback to "the workspace store has
 * never successfully loaded" confines it to the cold-boot window, where no switch has
 * happened and there is nothing stale to inherit. Once the workspace store has loaded,
 * the filter applies exactly as before.
 */
export const workspaceSubscriptionListSelector = createSelector(
  [
    (state: CommonRootState) => state.DBSubscriptionReducer,
    (state: CommonRootState) => selectCurrentUser(state),
    selectCurrentWorkspace,
    (state: CommonRootState) => state.DBWorkspaceReducer?.itemsLoaded === true,
  ],
  (subscriptionReducer: GenericState<Subscription>, user: UserInterface | undefined, currentWorkspace: Workspace | undefined, workspacesLoaded: boolean) => {
    let subscriptionListObjects: SubscriptionObject[] = [];

    const subscriptionList = currentWorkspace
      ? subscriptionSelectByQuery(subscriptionReducer, {
        $or: [
          {
            _id: {
              $in: currentWorkspace?.subscriptionList
            },
          },
          {
            subscription_id: currentWorkspace?.chargebeeSubscriptionId
          },
          {
            workspaceId: currentWorkspace?._id
          }
        ]

      })
      // Workspace doc not here yet AND the workspace store has never loaded: trust the
      // server's scoping rather than classify a paying customer as free.
      : (workspacesLoaded ? [] : subscriptionSelectByQuery(subscriptionReducer, {}));

    subscriptionListObjects = (subscriptionList ?? []).map(
      (subscription) => new SubscriptionObject(subscription)
    );

    subscriptionListObjects.sort((a, b) =>
      moment(b.createdAt).diff(moment(a.createdAt))
    );

    return subscriptionListObjects;
  }
);

export const didUserHaveTrialSubscription = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState) => selectCurrentUser(state),
    selectCurrentWorkspace,
  ],
  (state: CommonRootState, user: UserInterface | undefined, currentWorkspace: Workspace | undefined) => {
    const subscriptions = currentWorkspace
      ? subscriptionSelectByQuery(state.DBSubscriptionReducer, {
        _id: {
          $in: currentWorkspace?.subscriptionList,
        },
      })
      : [];

    const hasExpiredTrialSubscription = subscriptions.some(
      (subscription) =>
        subscription.type === "trial" &&
        moment(subscription.expiredDate).isBefore(moment())
    );

    return hasExpiredTrialSubscription;
  }
);

export const didUserHaveRealSubscription = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState) => selectCurrentUser(state),
    selectCurrentWorkspace,
  ],
  (state: CommonRootState, user: UserInterface | undefined, currentWorkspace: Workspace | undefined) => {
    const subscriptions = currentWorkspace
      ? subscriptionSelectByQuery(state.DBSubscriptionReducer, {
        _id: {
          $in: currentWorkspace?.subscriptionList,
        },
      })
      : [];

    const hasRealSubscription = subscriptions.some(subscription => subscription.type !== "trial");

    return hasRealSubscription;
  }
);


export const latestWorkspaceSubscriptionSelector = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState) => workspaceSubscriptionListSelector(state),
  ],
  (
    state: CommonRootState,
    subscriptionList: SubscriptionObject[]
  ): SubscriptionObject | undefined => {
    const sortedSubscriptionList = (subscriptionList ?? []).map(
      (subscription) => new SubscriptionObject(subscription)
    );
    sortedSubscriptionList.sort((a, b) =>
      moment(b.updatedAt).diff(moment(a.updatedAt))
    );

    const [latestWrokspaceSubscription] = sortedSubscriptionList;
    return latestWrokspaceSubscription;
  }
);

export const activeSubscriptionSelector = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState) => workspaceSubscriptionListSelector(state),
  ],
  (
    state: CommonRootState,
    subscriptionList: SubscriptionObject[]
  ): SubscriptionObject | undefined => {
    const sortedSubscriptionList = (subscriptionList ?? [])
      .map((subscription) => new SubscriptionObject(subscription))
      .filter((subscription) => subscription?.isActive);

    sortedSubscriptionList.sort((a, b) =>
      moment(b.createdAt).diff(moment(a.createdAt))
    );

    // Then, prioritize "chargebee" subscriptions
    sortedSubscriptionList.sort((a, b) => {
      if (a.type === 'chargebee' && b.type !== 'chargebee') return -1;
      if (a.type !== 'chargebee' && b.type === 'chargebee') return 1;
      return 0;
    });

    const [activeSubscription] = sortedSubscriptionList;
    return activeSubscription;
  }
);

// latest subscription selector (including expired ones) - for admin billing display
export const latestSubscriptionSelector = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState) => workspaceSubscriptionListSelector(state),
  ],
  (
    state: CommonRootState,
    subscriptionList: SubscriptionObject[]
  ): SubscriptionObject | undefined => {
    const sortedSubscriptionList = (subscriptionList ?? [])
      .map((subscription) => new SubscriptionObject(subscription));

    sortedSubscriptionList.sort((a, b) =>
      moment(b.createdAt).diff(moment(a.createdAt))
    );

    // Then, prioritize "chargebee" subscriptions
    sortedSubscriptionList.sort((a, b) => {
      if (a.type === 'chargebee' && b.type !== 'chargebee') return -1;
      if (a.type !== 'chargebee' && b.type === 'chargebee') return 1;
      return 0;
    });

    // Return the latest subscription (could be active or inactive)
    const [latestSubscription] = sortedSubscriptionList;
    return latestSubscription;
  }
);

// Hard-block the UI when the account is delinquent (>=1 unpaid invoice, >9 days overdue).
// Evaluated against the latest subscription INCLUDING cancelled ones — Chargebee cancels a
// delinquent sub after its dunning window (status → Free), but it still owes and must stay
// blocked until settled. See computeAccountBlocked for the supersede-on-resubscribe rule.
export const accountBlockedSelector = createSelector(
  [(state: CommonRootState) => workspaceSubscriptionListSelector(state)],
  (subscriptionList: SubscriptionObject[]): boolean => computeAccountBlocked(subscriptionList)
);

// Payment-failed (dunning) warning banner state. Driven by the SAME latest subscription as
// accountBlockedSelector — so the warning shows for a debt on a cancelled sub that the active
// subscription would mask, and the banner and the hard block can never disagree. See computeDunning.
export const dunningSelector = createSelector(
  [(state: CommonRootState) => workspaceSubscriptionListSelector(state)],
  (subscriptionList: SubscriptionObject[]): DunningState => computeDunning(subscriptionList)
);

// all workspace subscriptions selector (including expired ones)
export const allWorkspaceSubscriptionsSelector = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState) => workspaceSubscriptionListSelector(state),
  ],
  (
    state: CommonRootState,
    subscriptionList: SubscriptionObject[]
  ): SubscriptionObject[] => {
    const sortedSubscriptionList = (subscriptionList ?? [])
      .map((subscription) => new SubscriptionObject(subscription));

    sortedSubscriptionList.sort((a, b) =>
      moment(b.createdAt).diff(moment(a.createdAt))
    );

    // Then, prioritize "chargebee" subscriptions
    sortedSubscriptionList.sort((a, b) => {
      if (a.type === 'chargebee' && b.type !== 'chargebee') return -1;
      if (a.type !== 'chargebee' && b.type === 'chargebee') return 1;
      return 0;
    });

    return sortedSubscriptionList;
  }
);

// active subscription type selector
export const activeSubscriptionTypeSelector = createSelector(
  activeSubscriptionSelector,
  (activeSubscription: SubscriptionObject | undefined) => activeSubscription?.type
);

export const latestSubscriptionTypeSelector = createSelector(
  latestSubscriptionSelector,
  (latestSubscription: SubscriptionObject | undefined) => latestSubscription?.type
);

export const latestChargebeeSubscriptionSelector = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState) => subscriptionObjectListSelector(state),
  ],
  (
    state,
    subscriptionList: SubscriptionObject[]
  ): SubscriptionObject | undefined => {
    const sortedSubscriptionList = (subscriptionList ?? []).filter(
      (subscription) => subscription?.type === "chargebee"
    );

    sortedSubscriptionList.sort((a, b) =>
      moment(b.createdAt).diff(moment(a.createdAt))
    );

    const [latestChargebeeSubscription] = sortedSubscriptionList;
    return latestChargebeeSubscription;
  }
);

export const workspaceChargebeeSubscriptionSelector = createSelector(
  [
    (state: CommonRootState) => state,
    (state: CommonRootState) => workspaceSubscriptionListSelector(state),
  ],
  (
    state: CommonRootState,
    subscriptionList: SubscriptionObject[]
  ): SubscriptionObject | undefined => {
    const chargebeeSubscriptions = (subscriptionList ?? [])
      .map((subscription) => new SubscriptionObject(subscription))
      .filter((subscription) => subscription?.type === "chargebee");

    chargebeeSubscriptions.sort((a, b) =>
      moment(b.createdAt).diff(moment(a.createdAt))
    );

    const [latestWorkspaceChargebeeSubscription] = chargebeeSubscriptions;
    return latestWorkspaceChargebeeSubscription;
  }
);

export const hasActiveSubscriptionSelector = createSelector(
  [activeSubscriptionSelector],
  (activeSubscription: SubscriptionObject | undefined) => activeSubscription !== undefined
);

export const hasInactiveSubscriptionSelector = createSelector(
  [
    (state: CommonRootState) => subscriptionSelectByQuery(state.DBSubscriptionReducer, {}),
    hasActiveSubscriptionSelector,
  ],
  (subscriptionList: Subscription[], hasActiveSubscription: boolean) =>
    hasActiveSubscription && subscriptionList.length > 0
);

export const messageLimitSelector = createSelector(
  activeSubscriptionSelector,
  (activeSubscription: SubscriptionObject | undefined) =>
    activeSubscription?.messageLimit ?? 1
);

/**
 * FE#167 — is the scheduling entitlement PAID, FREE, or NOT KNOWN YET?
 *
 * `messageLimitSelector` above cannot answer this on its own: its absence fallback is
 * `1`, which IS the free-plan limit, so "we have not loaded the subscription" and "this
 * user is on the free plan" are the same value. That collapse is the whole of #167.
 *
 * The tri-state signal already existed in the store and was simply never read:
 * `DBSubscriptionReducer.itemsLoaded` is set true ONLY on a successful find
 * (DBServiceReducers.ts:459); the catch (:470) clears `itemsLoading` and leaves
 * `itemsLoaded` false. So a rejected/never-issued `/subscriptions` fetch is
 * distinguishable from "loaded, zero rows" without any new state.
 *
 * DELIBERATELY NOT PERMISSIVE: `unknown` does not grant paid. Its only admissible
 * behaviour is HOLD — no paywall AND no composer — so an outage can neither accuse a
 * paying customer of being free nor hand a free user an unlimited plan.
 */
export type ScheduleEntitlement = 'paid' | 'free' | 'unknown';

export const scheduleEntitlementSelector = createSelector(
  [
    messageLimitSelector,
    (state: CommonRootState) => state.DBSubscriptionReducer?.itemsLoaded === true,
  ],
  (messageLimit: number, subscriptionsLoaded: boolean): ScheduleEntitlement => {
    if (messageLimit === Infinity) return 'paid';
    if (!subscriptionsLoaded) return 'unknown';
    return 'free';
  }
);

/**
 * FE#167 / #159 / #156 — the SHARED entitlement tri-state.
 *
 * `scheduleEntitlementSelector` above solved this for scheduling only. The same defect
 * shipped three times because every gate re-derived "am I entitled?" from an
 * optional-chained subscription and picked the unentitled value on absence:
 *
 *   #167  activeSubscription?.messageLimit ?? 1        -> payer blocked from scheduling
 *   #159  activeSubscription?.hasSocket()  ?? false    -> Pro user sold "work offline"
 *   #156  activeSubscription?.order        ?? 0        -> payer shown "Downgrade", cannot subscribe
 *
 * Absence is not "free", it is "not known yet". Re-deriving that judgement per gate is
 * what produced three instances, so it lives here once and gates consume it.
 *
 * Keyed on `DBSubscriptionReducer.itemsLoaded`, which is set true ONLY on a successful
 * find; the catch leaves it false. All three gates read `order`/`hasSocket`/`messageLimit`
 * off the SAME subscription doc, so one flag covers the trio — verified, not assumed:
 * plan-selection reads `activeSubscription?.order`, not a separate plan catalogue.
 *
 * NOT PERMISSIVE. `unknown` never means paid. Optimistic display was considered and
 * DEFERRED: the free cap has no server-side enforcement today
 * (`services/experiments/free-limit.ts` — "this is a UI gate. The backend does NOT reject
 * over-limit creates"), so rendering unrestricted on unknown would make the cap advisory.
 * Revisit only once the server enforces at schedule/send time.
 */
export type EntitlementStatus = 'paid' | 'free' | 'unknown';

export const entitlementStatusSelector = createSelector(
  [
    activeSubscriptionSelector,
    (state: CommonRootState) => state.DBSubscriptionReducer?.itemsLoaded === true,
  ],
  (activeSubscription: SubscriptionObject | undefined, loaded: boolean): EntitlementStatus => {
    if (activeSubscription !== undefined) return 'paid';
    if (!loaded) return 'unknown';
    return 'free';
  }
);

/** True while entitlement is unresolved. The ONLY admissible behaviour is to hold —
 *  render no paywall, no upsell, no restriction, and grant no paid capability. */
export const entitlementUnknownSelector = createSelector(
  [entitlementStatusSelector],
  (status: EntitlementStatus) => status === 'unknown'
);

export const isOverLimitSelector = createSelector(
  (_state: CommonRootState, messageCount: number) => messageCount,
  messageLimitSelector,
  (messageCount: number, messageLimit: number) => messageCount > messageLimit
);



export const selectUserId = createSelector(
  [(state: CommonRootState) => state],
  (state: CommonRootState) => {
    const [user] = userSelectByQuery(state.DBUserReducer, {
      _id: state.UserReducer.userId,
    });
    return user?._id;
  }
);



export const selectCurrentUserSession = createSelector(
  [
    (state: CommonRootState) => state.DBSessionReducer,
    (state: CommonRootState) => state.UserReducer?.userId,
  ],
  (sessionState, userId) => {
    debugLog('connected-engines', 'selectCurrentUserSession: computing', {
      hasSessionState: !!sessionState,
      userId,
      sessionStateKeys: sessionState ? Object.keys(sessionState).slice(0, 5) : []
    });
    
    const session = sessionSelectOneObjectByQuery(
      sessionState,
      {
        owner: userId,
        //status: { $in: ["qrcode", "connected"] },
      },
      { updatedAt: -1 }
    );
    
    debugLog('connected-engines', 'selectCurrentUserSession: result', {
      sessionId: session?._id,
      sessionStatus: session?.status,
      hasSession: !!session
    });
    
    return session;
  }
);

export const selectCurrentSessionQrCode = createSelector(
  selectCurrentUserSession,
  (session) => session?.qrcode
);

export const selectCurrentSessionId = createSelector(
  selectCurrentUserSession,
  (session) => session?._id
);

export const selectCurrentSessionStatus = createSelector(
  selectCurrentUserSession,
  (session) => session?.status
);

// Anchored to the lastSeen heartbeat (shared deriveDisplayStatus) instead of raw session.status, so the
// top-bar indicator no longer flickers on transient MQTT reconnect blips — it only shows disconnected
// after a real >6-min silence. DRY: same derivation the Engines-Management list uses. `now` comes from a
// throttled tick the component passes, so a stale session flips to disconnected over time.
export const selectIsCurrentUserSessionConnected = createSelector(
  [selectCurrentUserSession, selectNow],
  (session, now) => !!session?.active && isDisplayConnected(session, now)
);






export const getUserStartDayOfWeek = createSelector([selectUser], (user) => {
  if(user?.preferences?.calendarStartDay || user?.preferences?.calendarStartDay === 0){
    return user?.preferences?.calendarStartDay;
  }else{
    const newStartDay = getSystemFirstDayOfWeek();
    return newStartDay;
  }
});

export const getSystemFirstDayOfWeek = () => {
  const systemLocale = (navigator.language || "en-US").toLowerCase();
  const normalizedLocale = systemLocale.split("-")[0];
  // Do not mutate global Moment locale here; use localeData lookup only.
  const localeData = moment.localeData(systemLocale) || moment.localeData(normalizedLocale) || moment.localeData("en");
  const firstDay = localeData.firstDayOfWeek();
  return firstDay === 0 ? 0 : 1; // Return 0 for Sunday, 1 for Monday
};

// Select user role
export const selectUserRole = createSelector([selectUser], (user) => {
  return user?.role;
});

// Select default scheduling time from user preferences
export const selectDefaultSchedulingTime = createSelector([selectUser], (user) => {
  return user?.preferences?.defaultSchedulingTime;
});
